import React, { Component } from 'react'
import { graphql } from 'react-apollo'
import compose from 'lodash/flowRight'
import { ThemeProvider } from 'styled-components'
import Color from 'color'
import { translate } from "react-i18next"
import { connect } from 'react-redux'
import * as moment from 'moment-timezone'

import BusinessLogo from './BusinessLogo'
import NotFoundPage from 'cogsworth/pages/NotFoundPage/NotFoundPage'
import Loading from 'cogsworth/components/Loading/Loading'
import BookingForm from 'cogsworth/components/BookingForm/BookingForm'
import BookingConfirmation from 'cogsworth/components/BookingConfirmation/BookingConfirmation'
import BookingPayment from 'cogsworth/components/BookingPayment/BookingPayment'
import RescheduleScreen from 'cogsworth/components/RescheduleScreen/RescheduleScreen'
import PrivacyNotice from 'cogsworth/components/PrivacyNotice/PrivacyNotice'
import ResetButton from 'cogsworth/components/ResetButton/ResetButton'
import { setEndpoint, setPaymentError, setSavedAppointment } from 'cogsworth/utils/store'
import { getDateUrlParameter } from 'cogsworth/utils/filters'
import { redirectGet, redirectPost } from 'cogsworth/utils/redirect'
import { paymentIdFor, transactionUrlFor } from 'cogsworth/utils/payments'

import getBusinessQuery from 'graphql/queries/getBusiness'
import ATTACH_PAYMENT_MUTATION from 'graphql/mutations/attachPaymentToAppointment'

import './BookingPage.css'
import { TIMEZONES_COUNTRIES, MOMENT_LOCALES } from 'cogsworth/constants.js'

import "moment/locale/en-au"
import "moment/locale/de"
import "moment/locale/ru"
import "moment/locale/es"
import "moment/locale/fr"
import "moment/locale/ja"
import "moment/locale/pt"
import "moment/locale/it"
import "moment/locale/fa"
import "moment/locale/zh-cn"
import "moment/locale/pl"
import "moment/locale/nl"
import "moment/locale/tr"
import "moment/locale/cs"
import "moment/locale/ko"
import "moment/locale/ar"
import "moment/locale/vi"
import "moment/locale/sv"
import "moment/locale/el"
import "moment/locale/hu"
import "moment/locale/nb"
import "moment/locale/ro"
import "moment/locale/sl"
import "moment/locale/he"
import "moment/locale/id"

class BookingPage extends Component {
  constructor(props){
    super(props)

    this.state = {
      id: null,
      loading: true,
      savedAppointment: props.savedAppointment ? props.savedAppointment : null,
      cancelledAppointment: null,
      pageType: null,
      error: null,
      reschedulingAppointment: null,
      rescheduling: false,
      countryCode: null,
      rescheduleFutureOccurrences: false,
      payment: null,
      paymentProvider: null,
      ready: false,
    }

    this.setPageTitle = this.setPageTitle.bind(this)
    this.handleAppointmentSaved = this.handleAppointmentSaved.bind(this)
    this.handleReschedule = this.handleReschedule.bind(this)
    this.handleCancelled = this.handleCancelled.bind(this)
    this.handlePaymentComplete = this.handlePaymentComplete.bind(this)
    this.handlePaymentDeclined = this.handlePaymentDeclined.bind(this)
    this.setError = this.setError.bind(this)
  }

  componentDidMount() {
    this.setState({
      countryCode: this.getCountryCode()
    })

    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);

    if (params.get('reset') === "true") {
      const newUrl = window.location.href.replace('?reset=true', '').replace('&reset=true', '')
      window.history.pushState({}, document.title, newUrl);
      this.props.dispatch({ type: 'RESET' })
    }
  }

  getCountryCode(){
    const timezone = moment.tz.guess()
    const code = TIMEZONES_COUNTRIES[timezone]
    return code
  }

  trackBusinessGoogleAnalyticsEvent(business){
    if(!business.googleTrackingId || !business.availableFeatures.googleAnalytics) {
      return
    }

    if(!window.gtag) {
      return
    }

    window.gtag('set', {
      'cookie_flags': 'SameSite=None;Secure'
    });
    window.gtag('config', business.googleTrackingId)
  }

  componentWillReceiveProps(nextProps) {
    const { data:oldData } = this.props
    const { data = {}, dispatch, appEndpoint, appPage } = nextProps

    if (nextProps.savedAppointment === null && this.props.savedAppointment !== null) {
      this.setState({  savedAppointment: null })
    }

    // Don't go any further if the page is still loading
    if (data.loading) {
      return
    }

    if (data.error && data.error.graphQLErrors.some(e => e.message === "Endpoint not found.")) {
      this.setState({ loading: false, error: 'endpoint-not-found' })
      return
    }

    // Check if business is not found
    if (!data.business) {
      this.setState({ loading: false, error: 'business-not-found' })
      return
    }

    // Everything past here requires the endpoint, so stop
    if (!data.endpoint) {
      return
    }

    // Define resources that are shared between all the next logic blocks
    const { appointment, business, endpoint } = data
    const pageType = endpoint.resourceType.toLowerCase()

    // On a fresh page load, or a reset, the endpoint needs setting
    const appEndpointId = appEndpoint && appEndpoint.resourceId
    if (data.endpoint.resourceId && data.endpoint.resourceId !== appEndpointId) {
      dispatch(setEndpoint(data.endpoint.resourceId, data.endpoint.resourceType.toLowerCase()))
    }
    if (!oldData.loading && data.endpoint.resourceId === appEndpointId) {
      this.setState({ ready: true })
    }

    // Pre-set state with data from appointment
    // TODO: DRY this up
    let pageState = {}
    if (pageType === 'staff' || pageType === 'service') {
      pageState = {
        [`booking_${pageType}`]: endpoint.resourceId,
      }
    } else if (appointment && appointment.found) {
      pageState = {
        booking_service: appointment.service.id,
        booking_duration: appointment.duration,
      }
    }
    const preselectedDay = getDateUrlParameter(window.location.search)
    if (preselectedDay) {
      pageState['booking_date'] = preselectedDay
    } else {
      delete pageState['booking_date']
    }
    if (JSON.stringify(pageState) !== JSON.stringify(appPage)) {
      dispatch({ type: 'SET', key: 'page', value: pageState })
    }

    // Old state was loading, now it's not
    if (oldData.loading && data.endpoint) {
      this.setPageTitle(business)

      // Track page view on business' Google Analytics
      this.trackBusinessGoogleAnalyticsEvent(business)

      const features = business.availableFeatures
      let error = null

      const reschedulingAppointment = pageType === 'appointment' ? appointment : null
      const staff = business.staffs.find(s => s.id === endpoint.resourceId)

      if (pageType === 'staff' && !staff) {
        error = 'staff-not-found'
      } else if (pageType === 'staff' && !staff.enabled) {
        error = 'staff-not-enabled'
      } else if (pageType === 'business' && !business.enabled) {
        error = 'business-not-enabled'
      } else if (pageType === 'service' && !business.services.find(s => s.id === endpoint.resourceId && !s.disabled)) {
        error = 'service-not-found'
      } else if (pageType === 'appointment' && reschedulingAppointment.found && reschedulingAppointment.tokenError) {
        error = 'incorrect-token'
      } else if (pageType === 'appointment' && reschedulingAppointment.noOcurrences) {
        error = 'no-ocurrences'
      } else if (pageType === 'appointment' && !reschedulingAppointment.found) {
        error = 'appointment-not-found'
      } else if (pageType === 'adhocservice' && !data.endpoint.hasAvailableTimes) {
        error = 'adhoc-service-unavailable'
      } else if (business.isLocked && !business.previewMode) {
        error = 'business-locked'
      }

      this.setState({
        id: endpoint.resourceId,
        loading: false,
        features,
        pageType,
        error,
        reschedulingAppointment,
        rescheduling: false,
        ready: appEndpointId === endpoint.resourceId,
      })

      // Set moment locale
      moment.locale(MOMENT_LOCALES[business.bookingLocale] || "en")

      // Set i18n locale
      this.props.i18n.changeLanguage(business.bookingLocale || "en")
    }
  }

  setError(error){
    this.setState({ error })
  }

  handlePaymentComplete(response, provider) {
    this.setState({
      payment: response,
      paymentProvider: provider,
      paymentError: null,
    }, this.attachPayment)
  }

  async attachPayment() {
    try {
      const { payment, paymentProvider } = this.state

      this.setState({ loading: true })

      const { data: { savedAppointment } } = await this.props.attachPayment({
        variables: {
          id: this.state.savedAppointment.id,
          token: this.state.savedAppointment.token,
          paymentProvider,
          paymentId: paymentIdFor(payment, paymentProvider),
          transactionUrl: transactionUrlFor({ payment, paymentProvider, business: this.props.data.business }),
        }
      })

      this.handleAppointmentSaved(savedAppointment)

      this.setState({ savedAppointment, loading: false })
    } catch(error) {
      console.error(error)
      alert(error)
    }
  }

  handlePaymentDeclined(error, provider) {
    const { dispatch } = this.props
    dispatch(setPaymentError(error))

    this.setState({
      paymentError: error,
      paymentProvider: provider,
    })
  }

  async handleAppointmentSaved(appointment) {
    if (appointment.pendingPayment) {
      this.setState({
        savedAppointment: appointment,
      })
      this.props.dispatch(setSavedAppointment(appointment))

      return
    }

    const { dispatch } = this.props
    const { redirectAfterBookingUrl, postOnRedirect, includeAnswersOnRedirect } = this.props.data.business
    const redirectUrl = this.state.features.redirectAfterBooking && redirectAfterBookingUrl

    dispatch({ type: 'RESET' })
    this.setState({
      savedAppointment: appointment,
      redirecting: redirectUrl ? true: false
    })

    await this.loadTrackingPixel({ business: this.props.data.business })

    if (redirectUrl && includeAnswersOnRedirect && postOnRedirect) {
      redirectPost(redirectUrl, appointment)
    } else if (redirectUrl) {
      redirectGet(redirectUrl, { includeAnswers: includeAnswersOnRedirect, data: appointment })
    }
  }

  loadTrackingPixel({ business }){
    if(!business.availableFeatures.trackingPixel || !business.trackingPixelUrl) {
      return null
    }

    return new Promise((resolve, reject) => {
      const img = new Image(1, 1)
      img.onload = resolve
      img.onerror = resolve
      img.src = business.trackingPixelUrl
    })
  }

  setPageTitle(business){
    document.title = business.name + ' Booking Page '
    const staff = business.staffs.find(u => u.id === this.state.id)
    if( staff ) document.title = document.title + ' - ' + staff.name

    document.querySelector('meta[name="title"]').setAttribute("content", business.name)
  }

  handleReschedule({ rescheduleFutureOccurrences }){
    this.setState({
      rescheduling: true,
      rescheduleFutureOccurrences,
    })
  }

  handleCancelled(){
    this.setState({
      cancelledAppointment: this.state.reschedulingAppointment
    })
  }

  pageClassNameFor(business) {
    let className = 'booking-page'

    if (business.logoPosition) {
      className = className + ' logo-' + business.logoPosition.toLowerCase()
    }

    return className
  }

  getBookingForm() {
    const { appointment, business } = this.props.data
    const { id, features, pageType } = this.state

    return (
      <BookingForm
        business={business}
        appointment={appointment.found ? appointment : null}
        id={id}
        features={features}
        pageType={pageType}
        onSave={this.handleAppointmentSaved}
        rescheduling={this.state.reschedulingAppointment}
        rescheduleFutureOccurrences={this.state.rescheduleFutureOccurrences}
        countryCode={this.state.countryCode}
        setError={this.setError}
      />
    )
  }

  getRescheduleScreen() {
    const { business } = this.props.data
    const { reschedulingAppointment } = this.state

    return (
      <RescheduleScreen
        business={business}
        appointment={reschedulingAppointment}
        onReschedule={this.handleReschedule}
        onCancel={this.handleCancelled}
        token={this.props.match.params.token}
      />
    )
  }

  getBookingConfimationScreen() {
    const { business } = this.props.data
    const { savedAppointment, cancelledAppointment } = this.state

    return (
      <BookingConfirmation
        business={business}
        savedAppointment={savedAppointment}
        cancelledAppointment={cancelledAppointment}
        payment={this.state.payment}
        paymentProvider={this.state.payment}
        paymentError={this.state.paymentError}
      />
    )
  }

  getPaymentScreen() {
    return (
      <BookingPayment
        business={this.props.data.business}
        booking={this.state.savedAppointment}
        onPaymentComplete={this.handlePaymentComplete}
        onPaymentDeclined={this.handlePaymentDeclined}
        paymentError={this.state.paymentError}
      />
    )
  }

  bookingProcessIsFinished() {
    const { savedAppointment, cancelledAppointment } = this.state

    if (cancelledAppointment) {
      return true
    }

    if (!savedAppointment) {
      return false
    }

    // Payment was saved and no payment pending.
    if (!savedAppointment.pendingPayment) {
      return true
    }

    // Payment was pending payment, but it is now paid.
    if (savedAppointment.pendingPayment && this.state.payment) {
      return true
    }

    return false
  }

  render() {
    const { error, rescheduling, reschedulingAppointment } = this.state
    const { endpoint } = this.props
    const { business } = this.props.data

    if (error) {
      return <NotFoundPage error={error} business={business} />
    }

    // When initially loading and resetting state, endpoint is not defined yet
    if (endpoint === null || this.props.data.loading || this.state.loading || this.state.redirecting || !this.state.ready) {
      return <Loading />
    }

    const { features, savedAppointment } = this.state

    const bookingProcessIsFinished = this.bookingProcessIsFinished()
    let component = null
    if (bookingProcessIsFinished) {
      component = this.getBookingConfimationScreen()
    } else if (savedAppointment && savedAppointment.pendingPayment) {
      component = this.getPaymentScreen()
    } else if (reschedulingAppointment && !rescheduling) {
      component = this.getRescheduleScreen()
    } else {
      component = this.getBookingForm()
    }

    const mainColor = (features.customColor && business.color) ? business.color : '#4e9932'
    const theme = {
      main: mainColor,
      hover: Color(mainColor).darken(0.25).string()
    }

    return (
      <div className={this.pageClassNameFor(business)}>
        <ThemeProvider theme={theme}>
          <div className="booking-page-content">
            <div className="booking-page-header">
              <BusinessLogo business={business} features={features} position="HEADER_START" />
            </div>
            { component }
            <div className="booking-page-footer">
              <BusinessLogo business={business} features={features} position="FOOTER_START" />
              <div className="buttons">
                { !bookingProcessIsFinished && <ResetButton /> }
                { !business.hidePrivacyButton && <PrivacyNotice /> }
              </div>
              <BusinessLogo business={business} features={features} position="FOOTER_END" />
            </div>
          </div>
        </ThemeProvider>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  appEndpoint: state.endpoint,
  appPage: state.page,
  savedAppointment: state.savedAppointment,
})
export default connect(mapStateToProps)(compose(
  graphql(getBusinessQuery,
    {
      options: (props) => ({
        variables: {
          endpointId: props.match.params.id,
          appointmentId: props.match.params.token ? props.match.params.id : undefined,
          token: props.match.params.token
        }
      })
    }
  ),
  graphql(ATTACH_PAYMENT_MUTATION, { name: 'attachPayment' })
)(translate("translations")(BookingPage)))
